import { useState, useEffect, useCallback } from 'react';
import { Drawer, Stack, Text, Loader, Center, Group, Badge, Pagination } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { rateCardApi } from '../../../../services/api/rateCard';
import type { RateCardActivityLogEntry } from '../../../../types';

interface ActivityLogDrawerProps {
  opened: boolean;
  onClose: () => void;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString()}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function ActivityLogDrawer({ opened, onClose }: ActivityLogDrawerProps) {
  const [entries, setEntries] = useState<RateCardActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchLog = useCallback(async () => {
    try {
      setLoading(true);
      const result = await rateCardApi.getActivityLog({ page, limit: 15 });
      setEntries(result.data);
      setTotalPages(result.pagination?.totalPages || 1);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load activity log',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (opened) {
      fetchLog();
    }
  }, [opened, fetchLog]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          Rate Card Activity Log
        </Text>
      }
      position="right"
      size="md"
    >
      <Stack gap="md">
        {loading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : entries.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No activity recorded yet.
          </Text>
        ) : (
          <>
            {entries.map((entry) => (
              <Stack
                key={entry.id}
                gap="xs"
                p="sm"
                style={{ borderBottom: '1px solid #E8E6E1' }}
              >
                <Group justify="space-between" align="flex-start">
                  <Text size="sm" fw={600}>
                    {entry.performed_by_name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {formatDate(entry.created_at)}
                  </Text>
                </Group>
                <Text size="sm" c="dimmed">
                  {entry.description}
                </Text>
                {entry.changes && entry.changes.length > 0 && (
                  <Stack gap={4}>
                    {entry.changes.map((change, idx) => (
                      <Group key={idx} gap="xs">
                        <Badge size="xs" variant="light" color="gray">
                          {change.category === 'flat'
                            ? change.flat_key
                            : change.category === 'one_on_one_sda'
                              ? `${change.session_type} / ${change.audience_type} / ${change.duration}min`
                              : `${change.category} / ${change.audience_type} / ${change.duration}min`}
                        </Badge>
                        <Text size="xs" td="line-through" c="dimmed">
                          {formatCents(change.old_value)}
                        </Text>
                        <Text size="xs">→</Text>
                        <Text size="xs" fw={600}>
                          {formatCents(change.new_value)}
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Stack>
            ))}

            {totalPages > 1 && (
              <Center>
                <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
              </Center>
            )}
          </>
        )}
      </Stack>
    </Drawer>
  );
}
